import Skeleton from "@components/Atoms/ImageSkeleton";
import { ResourceError } from "@core-ui/ContextServices/ResourceService/errors";
import { useGetResource } from "@core-ui/ContextServices/ResourceService/hooks/useGetResource";
import useObjectUrl from "@core-ui/hooks/useObjectUrl";
import { resolveFileKind } from "@core-ui/utils/resolveFileKind";
// biome-ignore lint/style/noRestrictedImports: expected
import styled from "@emotion/styled";
import InlineCommentView from "@ext/markdown/elements/comment/edit/components/View/InlineCommentView";
import InlineImageError from "@ext/markdown/elements/inlineImage/render/components/InlineImageError";
import { type ReactNode, useState } from "react";

interface InlineImageProps {
	src: string;
	alt: string;
	width: string;
	height: string;
	commentId?: string;
	isPrint?: boolean;
	renderSrc?: string;
}

interface SkeletonWrapperProps {
	children: ReactNode;
	width: string;
	height: string;
	isLoaded: boolean;
}

const calculateDisplaySize = (width: string, height: string, maxHeight = "1.7em") => {
	const maxHeightPx = parseFloat(maxHeight) * 16;
	const widthNum = parseFloat(width);
	const heightNum = parseFloat(height);

	if (heightNum <= maxHeightPx) return { width, height };

	const scale = maxHeightPx / heightNum;
	const scaledWidth = widthNum * scale;

	return {
		width: `${scaledWidth}px`,
		height: maxHeight,
	};
};

const SkeletonWrapper = ({ children, width, height, isLoaded }: SkeletonWrapperProps) => {
	const displaySize = calculateDisplaySize(width, height);

	return (
		<Skeleton elementType="span" height={displaySize.height} isLoaded={isLoaded} width={displaySize.width}>
			{children}
		</Skeleton>
	);
};

const ContainerWrapper = styled(InlineCommentView)`
	display: inline-block;
	border-radius: var(--radius-small);
	vertical-align: middle;
	max-height: 1.7em;
	overflow: hidden;
	border-bottom: none !important;

	&[data-comment="true"].inline-comment-view {
		outline: 2px solid var(--color-comment-block-border) !important;
	}

	&[data-comment="true"].inline-comment-view.active {
		outline: 2px solid var(--color-comment-block-hover-border) !important;
	}

	img {
		max-height: 1.7em;
		object-fit: contain;
	}
`;

const InlineImage = (props: InlineImageProps) => {
	const { src: initialSrc, alt, width, height, commentId, isPrint, renderSrc: propRenderSrc } = props;
	const renderSrc = isPrint ? null : propRenderSrc;
	const [isLoaded, setIsLoaded] = useState(false);
	const [error, setError] = useState<ResourceError | null>(null);
	const { url: objectUrl, setBlob } = useObjectUrl();
	const src = objectUrl ?? renderSrc ?? null;

	const onLoad = () => {
		setIsLoaded(true);
	};

	const onError = () => {
		if (!src) return;
		setError(new ResourceError("Image error", initialSrc));
	};

	useGetResource(
		(buffer, resourceError) => {
			if (resourceError || !buffer || !buffer.byteLength)
				return setError(resourceError ?? new ResourceError("Image error", initialSrc));
			if (isLoaded) setIsLoaded(false);

			setBlob(new Blob([buffer as BlobPart], { type: resolveFileKind(buffer) }));
		},
		initialSrc,
		undefined,
		undefined,
		isPrint,
		!!renderSrc,
	);

	const displaySize = calculateDisplaySize(width, height);

	return (
		<ContainerWrapper commentId={commentId}>
			<SkeletonWrapper height={height} isLoaded={!!error || isLoaded} width={width}>
				{error ? (
					<InlineImageError height={displaySize.height} resourceError={error} width={displaySize.width} />
				) : (
					<img alt={alt} data-focusable="true" onError={onError} onLoad={onLoad} src={src} />
				)}
			</SkeletonWrapper>
		</ContainerWrapper>
	);
};

export default InlineImage;
